/**
 * RecurringInspectionsPage - List view for Inspection recurring schedules.
 *
 * Mirror of RecurringWorkOrdersPage — the two share the same template so
 * adding a new series type later only requires copying this file and
 * swapping the type string.
 *
 * @route /recurring-inspections
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { recurrenceSeriesService } from '../../services/recurrenceSeriesService';
import type { RecurrenceSeriesRecord } from '../../services/recurrenceSeriesService';
import { RepeatsListTemplate } from '../../components/templates/RepeatsListTemplate';
import { qk } from '../../lib/queryKeys';

const RecurringInspectionsPage: React.FC = () => {
	const { hasAnyRole } = useAuth();

	const canCreate = hasAnyRole(['super_admin', 'admin']);

	const params = { type: 'inspection' };
	const query = useQuery({
		queryKey: qk.recurrenceSeries.list(params),
		queryFn: async () => {
			const res = await recurrenceSeriesService.list('inspection');
			return ((res.data as any)?.data ?? []) as RecurrenceSeriesRecord[];
		},
	});

	const series = query.data ?? [];
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	return (
		<RepeatsListTemplate
			className="recurring-inspections-page"
			dataRef="page-recurring-inspections-page"
			title="Recurring Inspections"
			subtitle="Google-Calendar-style schedules that materialize inspections ahead of time."
			createLabel="+ New Recurring Inspection"
			createRoute="/inspections/create"
			canCreate={canCreate}
			detailRoute={(id) => `/recurring-inspections/${id}`}
			series={series}
			isLoading={isLoading}
			error={error}
			emptyMessage={canCreate
				? 'No recurring inspections yet. Create an inspection and pick a Frequency to start a recurring schedule.'
				: 'No recurring inspections assigned to your company yet.'}
		/>
	);
};

export default RecurringInspectionsPage;
